import React, { useRef, useEffect } from 'react';
import Navbar from '../components/Navbar';

const teamMembers = [
  {
    name: "ANSADE",
    image: "/images/ansad.png",
    imageClass: "w-48 h-48 object-contain"
  },
  {
    name: "BFI",
    image: "/images/bfi.png",
    imageClass: "w-48 h-48 object-contain"
  },
  {
    name: "IPN",
    image: "/images/ipn.jpg",
    imageClass: "w-32 h-32 object-cover"
  },
  {
    name: "SOMELEK",
    image: "/images/somelek.webp",
    imageClass: "w-48 h-48 object-contain"
  },
  {
    name: "SUPNUM",
    image: "/images/supnum.png",
    imageClass: "w-32 h-32 object-cover"
  }
];

const certifications = [
  {
    name: "CCNA",
    description: "La CCNA est une certification de niveau débutant/intermédiaire proposée par Cisco, reconnue mondialement dans le domaine des réseaux informatiques",
    image: "/images/ccna.png",
    imageClass: "w-24 h-24 object-contain"
  },
  {
    name: "Fibre Optique",
    description: "C'est une formation spécialisée qui te permet de travailler avec les réseaux en fibre optique - une technologie utilisée pour transmettre Internet très rapidement",
    image: "/images/fib.avif",
    imageClass: "w-24 h-24 object-contain"
  }
];

const partnerships = [
  {
    name: "FortiGate",
    image: "/images/for.png",
    description: "FortiGate propose des pare-feux nouvelle génération alliant performance et intelligence artificielle pour protéger efficacement les environnements hybrides, les applications cloud et les données contre les cybermenaces avancées",
    imageClass: "w-48 h-48 object-contain"
  },
  {
    name: "Sophos",
    image: "/images/so.png",
    description: "Sophos est un leader mondial en cybersécurité, offrant des solutions de protection contre les menaces avancées. Notre partenariat nous permet de fournir une protection complète pour les endpoints, les réseaux et le cloud.",
    imageClass: "w-48 h-48 object-contain"
  }
];

const About = () => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.8;
    }
  }, []);

  return (
    <div className="min-h-screen">
      <Navbar />
      {/* Hero Section with Video Background */}
      <section className="relative bg-primary text-white h-[85vh] overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 w-full h-full">
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            autoPlay
            loop
            muted
            playsInline
          >
            <source src="/videos/b.mp4" type="video/mp4" />
            Votre navigateur ne supporte pas la lecture de vidéos.
          </video>
        </div>
      </section>

      {/* Clients Section with Plant Background */}
      <section className="relative py-12 bg-gradient-to-br from-green-50 via-white to-green-100">
        {/* Plant Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-64 h-64 bg-green-200/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-green-300/20 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-green-100/20 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <h3 className="text-3xl font-bold mb-8 text-center text-green-800">Nos clients importants</h3>
          <div className="flex justify-center items-center gap-8">
            {teamMembers.map((member, index) => (
              <div key={index} className="flex-none w-56 text-center bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-lg hover:shadow-xl transition-all duration-300 border border-green-100">
                <div className="flex justify-center items-center mb-3">
                  <img 
                    src={member.image} 
                    alt={member.name} 
                    className="w-48 h-48 object-contain"
                  />
                </div>
                <h4 className="font-bold text-xl text-green-800">{member.name}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-16 bg-gradient-to-br from-green-100 via-white to-green-50">
        <div className="container mx-auto px-4">
          <h3 className="text-3xl font-bold mb-12 text-center text-green-800">Nos Certifications</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {certifications.map((cert, index) => (
              <div key={index} className="flex items-center p-8 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-green-100">
                <div className="flex-shrink-0 mr-6">
                  <img 
                    src={cert.image} 
                    alt={cert.name} 
                    className={cert.imageClass}
                  />
                </div>
                <div>
                  <h4 className="font-bold text-xl text-green-800 mb-2">{cert.name}</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">{cert.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnerships Section */}
      <section className="py-16 bg-gradient-to-br from-green-100 via-white to-green-50">
        <div className="container mx-auto px-4">
          <h3 className="text-3xl font-bold mb-12 text-center text-green-800">Nos Partenariats Stratégiques</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {partnerships.map((partner, index) => (
              <div key={index} className="flex items-center p-6 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-green-100">
                <div className="flex-shrink-0 mr-4">
                  <img 
                    src={partner.image} 
                    alt={partner.name} 
                    className="w-32 h-32 object-contain"
                  />
                </div>
                <div>
                  <h4 className="font-bold text-xl text-green-800 mb-2">{partner.name}</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">{partner.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About; 